import React, { createContext, useContext, useState, ReactNode } from 'react';
import Modal from 'components/Modal';
import Button from 'components/Button';
import { TYPE } from 'theme';
import { RowBetween } from 'components/Row';
import styled from 'styled-components';

const StyledContent = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    min-width: 280px;
    max-width: 800px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        min-width: 80vw;
        max-width: calc(100vw - 2rem);
        padding: 1rem;
    `}
`;

interface ConfirmOptions {
    title?: string | null;
    content?: ReactNode; // simple content
    render?: (onConfirm: () => void, onClose: () => void) => ReactNode; // render props for interactive content
    confirmText?: string;
    cancelText?: string;
    cancelButton?: boolean;
    confirmButton?: boolean;
    shadowType?: 'box' | 'default';
    hideClose?: boolean;
}

const ModalContext = createContext({
    confirm: (options: ConfirmOptions) => Promise.resolve(false),
});

export const ModalProvider = ({ children }: { children: ReactNode }) => {
    const [modalState, setModalState] = useState<ConfirmOptions | null>(null);
    const [resolvePromise, setResolvePromise] = useState<(value: boolean) => void>();

    const confirm = (options: ConfirmOptions) => {
        return new Promise<boolean>((resolve) => {
            setModalState(options);
            setResolvePromise(() => resolve);
        });
    };

    const handleClose = (result: boolean) => {
        setModalState(null);
        resolvePromise?.(result);
    };

    return (
        <ModalContext.Provider value={{ confirm }}>
            {children}
            {modalState && (
                <Modal
                    isOpen={true}
                    onClose={() => handleClose(false)}
                    shadowProps={modalState.shadowType === 'box' ? { shadowType: 'box' } : true}
                    hideClose={modalState.hideClose ?? (modalState.cancelButton ? false : true)}
                >
                    <StyledContent>
                        {modalState.title && (
                            <TYPE.main
                                fontSize={20}
                                textAlign="center"
                                fontWeight={600}
                                marginBottom="20px"
                                marginTop="20px"
                            >
                                {modalState.title}
                            </TYPE.main>
                        )}
                        <div>
                            {modalState.content ||
                                modalState.render?.(
                                    () => handleClose(true),
                                    () => handleClose(false),
                                )}
                        </div>
                        <RowBetween gap="20px">
                            {modalState.cancelButton === false ? null : (
                                <Button
                                    onClick={() => handleClose(false)}
                                    size="small"
                                    type="outlineGradient"
                                    style={{ width: '100%', borderRadius: '30px' }}
                                >
                                    {modalState.cancelText || 'Cancel'}
                                </Button>
                            )}
                            {modalState.confirmButton === false ? null : (
                                <Button
                                    onClick={() => handleClose(true)}
                                    size="small"
                                    type="gradient"
                                    style={{ width: '100%', borderRadius: '30px' }}
                                >
                                    {modalState.confirmText || 'Confirm'}
                                </Button>
                            )}
                        </RowBetween>
                    </StyledContent>
                </Modal>
            )}
        </ModalContext.Provider>
    );
};

export const useModal = () => {
    return useContext(ModalContext);
};
